import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCameraGroup } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnCameraGroupsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCameraGroup>> {
    return this._httpClient.get('/api/camera-groups/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((group) => new SnCameraGroup(group)),
      })),
    );
  }

  public get(id: string): Observable<SnCameraGroup> {
    return this._httpClient
      .get(`/api/camera-groups/${id}/`)
      .pipe(map((response: any) => new SnCameraGroup(response)));
  }

  public create(data: any): Observable<SnCameraGroup> {
    if (data.id) {
      return this.update(data.id, data);
    }
    return this._httpClient.post('/api/camera-groups/', data).pipe(
      tap(() => this._toastrService.success(t('cameras.groupMessages.saved'))),
      map((response: any) => new SnCameraGroup(response)),
    );
  }

  public update(id: string, data: any): Observable<SnCameraGroup> {
    return this._httpClient.patch(`/api/camera-groups/${id}/`, data).pipe(
      tap(() => this._toastrService.success(t('cameras.groupMessages.saved'))),
      map((response: any) => new SnCameraGroup(response)),
    );
  }

  public delete(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.groupMessages.deleteConfirmMessage'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/camera-groups/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('cameras.groupMessages.deleted')),
          error: () =>
            this._toastrService.error(t('cameras.groupMessages.deleteError')),
        }),
      );
  }
}
